import { API } from "@swivl-mono/api-helper";
import { RoutingGroupInterface, TicketPreview, formatTicketNumberDisplay } from "@swivl-mono/types";
import { SearchInput } from "@swivl-mono/ui";
import React, { useEffect, useMemo, useState } from "react";
import { HiOutlineSearch } from "react-icons/hi";
import { useNavigate } from "react-router-dom";

import { useDebouncedValue } from "../../../Hooks/useDebouncedValue";
import LocationsModel from "../../../Models/Locations/Locations.Model";
import { Member } from "../../../Models/Member/Member.Current";
import MembersModel from "../../../Models/Member/Members.Model";
import { useTicketingStore } from "../../../Models/Ticketing/Ticketing.Store";
import { TicketFlyout } from "./TicketFlyout";
import { TicketSearchFilters } from "./TicketSearchFilters";
import { TicketSearchResults } from "./TicketSearchResults";
import { getMemberDisplayName } from "./useCreateTicket";

/** Full-width "Search All" page: keyword + filter chips + results table + flyout. */
export function TicketSearchView(): React.JSX.Element {
  const rows = useTicketingStore((s) => s.searchRows);
  const total = useTicketingStore((s) => s.searchTotal);
  const loading = useTicketingStore((s) => s.searchLoading);
  const searchQuery = useTicketingStore((s) => s.searchQuery);
  const setSearchQuery = useTicketingStore((s) => s.setSearchQuery);
  const refreshSearch = useTicketingStore((s) => s.refreshSearch);
  const clearSearch = useTicketingStore((s) => s.clearSearch);
  const selectTicket = useTicketingStore((s) => s.selectTicket);
  const navigate = useNavigate();

  const [keyword, setKeyword] = useState(searchQuery.q ?? "");
  const debouncedKeyword = useDebouncedValue(keyword, 300);
  const [flyoutTicket, setFlyoutTicket] = useState<TicketPreview | null>(null);

  // Filter option sources
  const members = MembersModel.useState((s) => s.members);
  const locations = LocationsModel.useState((s) => s.locations);
  const [groups, setGroups] = useState<RoutingGroupInterface[]>([]);

  useEffect(() => {
    void MembersModel.loadMembers();
    void LocationsModel.getGroupsAndLocations(false);
    let active = true;
    void API.RoutingGroup.listGroups(Member.orgId)
      .then((rows) => {
        if (active) setGroups(rows);
      })
      .catch(() => {
        if (active) setGroups([]);
      });
    return () => {
      active = false;
    };
  }, []);

  // Push the debounced keyword into the search query and re-run.
  useEffect(() => {
    setSearchQuery({ q: debouncedKeyword.trim() || undefined, offset: 0 });
    void refreshSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedKeyword]);

  const memberMap = useMemo(() => new Map((members ?? []).map((m) => [m.id, getMemberDisplayName(m)] as const)), [members]);
  const memberOptions = useMemo(() => (members ?? []).map((m) => ({ id: m.id, name: getMemberDisplayName(m) })), [members]);

  // Distinct, non-archived groups (a group may have rows at multiple scopes).
  const groupNameBySlug = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of groups) {
      if (g.isArchived) continue;
      if (!map.has(g.slug)) map.set(g.slug, g.name);
    }
    return map;
  }, [groups]);
  const groupOptions = useMemo(() => Array.from(groupNameBySlug.entries()).map(([slug, name]) => ({ slug, name })), [groupNameBySlug]);
  const locationOptions = useMemo(() => (locations ?? []).map((l) => ({ id: l.id, name: l.name })), [locations]);
  const locationMap = useMemo(() => new Map((locations ?? []).map((l) => [l.id, l.name] as const)), [locations]);

  const hasFilters = !!(
    searchQuery.assigneeMemberIds?.length ||
    searchQuery.routingGroupSlugs?.length ||
    searchQuery.statuses?.length ||
    searchQuery.locationIds?.length ||
    searchQuery.priority
  );
  const hasActiveSearch = !!keyword.trim() || hasFilters;

  const handleClear = (): void => {
    setKeyword("");
    clearSearch();
  };

  const handleRowClick = (ticketId: number): void => {
    const row = rows.find((r) => r.id === ticketId) ?? null;
    setFlyoutTicket(row);
    void selectTicket(ticketId);
  };

  const handleFlyoutClose = (): void => {
    // Only close the panel — keep the store selection so the ticket content
    // stays visible through the slide-out (clearing it would blank the panel
    // mid-animation). It's reset on the next row click or when leaving the page.
    setFlyoutTicket(null);
  };

  const handleExpand = (): void => {
    if (!flyoutTicket) return;
    navigate(`/inbox/ticket/${formatTicketNumberDisplay(flyoutTicket.number)}`);
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-white">
      <div className="shrink-0 p-4">
        <SearchInput
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onClear={() => setKeyword("")}
          placeholder="Search"
          containerClassName="w-full!"
        />
      </div>

      <div className="shrink-0 px-4 pb-3">
        <TicketSearchFilters
          memberOptions={memberOptions}
          groupOptions={groupOptions}
          locationOptions={locationOptions}
          showClear={hasActiveSearch}
          onClear={handleClear}
        />
      </div>

      <div className="flex min-h-0 flex-1 flex-col px-4 pb-4">
        {!hasActiveSearch ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 text-grey-600">
            <HiOutlineSearch className="h-10 w-10" />
            <div className="flex flex-col items-center gap-1 text-center">
              <p className="heading-base-alt text-grey-600">Search Tickets</p>
              <p className="body-sm">Type in a keyword or a ticket number.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="body-mono mb-2 shrink-0 text-black">
              {total} result{total === 1 ? "" : "s"} found
            </div>
            {/* Height hugs the row content when short; when the rows would
                exceed the available space, flex-shrink caps it and the table
                scrolls internally (down to the container's 16px bottom padding). */}
            <div className="min-h-0">
              <TicketSearchResults
                rows={rows}
                loading={loading}
                selectedId={flyoutTicket?.id ?? null}
                memberMap={memberMap}
                groupMap={groupNameBySlug}
                locationMap={locationMap}
                onRowClick={handleRowClick}
              />
            </div>
          </>
        )}
      </div>

      <TicketFlyout isOpen={flyoutTicket != null} onClose={handleFlyoutClose} onExpand={handleExpand} />
    </div>
  );
}
